﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_PAYMENT_PLANS_QUERY_KEY,
  GET_PAYMENT_PLANS,
  PAYMENT_PLAN_API_URL,
  PAYMENT_PLAN_BY_ID_QUERY_KEY
} from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import { CreateEditPaymentPlanPayload, ErrorResponse, Options } from '@/types';

export const paymentPlanEndpoints = {
  getAllPaymentPlans: (
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    sortingField?: string,
    sortingAsc?: boolean
  ) =>
    axios.get(
      `${apiUrl()}${PAYMENT_PLAN_API_URL}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'sorting.Field': sortingField as string,
        'sorting.Asc': sortingAsc as boolean,
        'search.Text': searchQuery as string
      })}`
    ),
  getById: (id: number) => axios.get(`${apiUrl()}${PAYMENT_PLAN_API_URL}/${id}`),
  deletePaymentPlan: (id: number) => axios.delete(`${apiUrl()}${PAYMENT_PLAN_API_URL}/${id}`),
  createPaymentPlan: (payload: CreateEditPaymentPlanPayload) =>
    axios.post(`${apiUrl()}${PAYMENT_PLAN_API_URL}`, payload),
  updatePaymentPlan: (payload: CreateEditPaymentPlanPayload) =>
    axios.put(`${apiUrl()}${PAYMENT_PLAN_API_URL}`, payload),
  setDefaultPaymentPlan: (id: number) =>
    axios.patch(`${apiUrl()}${PAYMENT_PLAN_API_URL}/${id}/set_default`)
};

export const useGetAllPaymentPlans = (
  pageSize: number,
  searchQuery?: string,
  sortingField?: string,
  sortingAsc?: boolean
) => {
  return useInfiniteQuery(
    [ALL_PAYMENT_PLANS_QUERY_KEY, { pageSize, searchQuery, sortingField, sortingAsc }],
    ({ pageParam }) =>
      paymentPlanEndpoints.getAllPaymentPlans(
        pageSize,
        pageParam,
        searchQuery,
        sortingField,
        sortingAsc
      ),
    {
      keepPreviousData: true,
      enabled: true,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useGetPaymentPlanById = (id: number, options?: Options) => {
  return useQuery([PAYMENT_PLAN_BY_ID_QUERY_KEY, { id }], () => paymentPlanEndpoints.getById(id), {
    enabled: !!id,
    retry: false,
    onSuccess: () => {
      options?.onSuccess();
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useDeletePaymentPlanMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(paymentPlanEndpoints.deletePaymentPlan, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([ALL_PAYMENT_PLANS_QUERY_KEY]);
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useCreatePaymentPlanMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(paymentPlanEndpoints.createPaymentPlan, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([GET_PAYMENT_PLANS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdatePaymentPlanMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(paymentPlanEndpoints.updatePaymentPlan, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([PAYMENT_PLAN_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useSetDefaultPaymentPlanMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(paymentPlanEndpoints.setDefaultPaymentPlan, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([ALL_PAYMENT_PLANS_QUERY_KEY]);
      queryClient.invalidateQueries([PAYMENT_PLAN_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};
